import React, { useState, useMemo, useEffect, useRef } from "react"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import { CollapsibleSection } from "@/components/CollapsibleSection"
import { hasCutoff } from "@/lib/preview"

import { useActiveSection } from "@/lib/useActiveSection"
import { Search, FileText, ChevronLeft, ChevronRight, List, Printer } from "lucide-react"
import type { DocumentSummary, DocumentDetail } from "@/lib/types"

interface ClinicalReviewsListViewProps {
  docs: DocumentSummary[]
  isLoading: boolean
  useDocumentDetail: (
    slug: string
  ) => { data: DocumentDetail | undefined; isLoading: boolean; isError: boolean }
  // Builds the PDF download URL; omit to hide the download affordance
  pdfUrl?: (slug: string) => string
}

function CRContent({
  doc,
  pendingScrollRef,
  pdfHref,
}: {
  doc: DocumentDetail
  pendingScrollRef?: React.MutableRefObject<string | null>
  pdfHref?: string
}) {
  const sectionKeys = doc.sections.map((s) => s.section_key)
  const activeSection = useActiveSection(sectionKeys)

  useEffect(() => {
    const sectionKey = pendingScrollRef?.current
    if (!sectionKey) return
    pendingScrollRef.current = null
    // Wait for sections to render, then scroll
    requestAnimationFrame(() => {
      document.getElementById(`section-${sectionKey}`)?.scrollIntoView({ behavior: "smooth" })
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Middle: scrollable content */}
      <div className="flex-1 min-w-0 overflow-auto">
        <div className="p-6">
          {/* Title + meta */}
          <div data-sticky-title className="sticky top-0 z-10 -mt-6 mb-6 bg-background pb-4 pt-6">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-2xl font-bold text-[#107D98]">{doc.title}</h2>
              {pdfHref && (
                <Button variant="ghost" size="sm" asChild className="shrink-0">
                  <a href={pdfHref}>
                    <Printer className="mr-2 h-4 w-4" />
                    Download PDF
                  </a>
                </Button>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2">
              {doc.published_at && (
                <span className="text-xs text-muted-foreground">
                  Last Updated:{" "}
                  {new Date(doc.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" })}
                </span>
              )}
              {doc.compliance_tags?.map((tag) => (
                <span key={tag} className="text-xs text-muted-foreground">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-6">
            {doc.sections.map((section, i) => (
              <CollapsibleSection
                key={section.id}
                section={section}
                previewCutoff={hasCutoff(doc.preview, doc.sections, i)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right: TOC */}
      <aside className="hidden w-72 shrink-0 px-6 lg:block xl:w-80">
        <div className="sticky top-0 p-4 pt-6">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <List className="h-4 w-4" />
            Page Contents
          </div>
          <ScrollArea className="h-[calc(100vh-5rem)]">
            <nav className="space-y-0.5">
              {doc.sections.map((section) => (
                <a
                  key={section.id}
                  href={`#section-${section.section_key}`}
                  className={`block border-l-2 py-1 pl-3 text-sm transition-colors ${
                    activeSection === section.section_key
                      ? "border-l-primary font-medium text-foreground"
                      : "border-l-transparent text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={(e) => {
                    e.preventDefault()
                    document
                      .getElementById(`section-${section.section_key}`)
                      ?.scrollIntoView({ behavior: "smooth" })
                  }}
                >
                  {section.heading}
                </a>
              ))}
            </nav>
          </ScrollArea>
        </div>
      </aside>
    </>
  )
}

function CRDetailPanel({
  slug,
  useDocumentDetail,
  pendingScrollRef,
  pdfUrl,
}: {
  slug: string
  useDocumentDetail: (
    slug: string
  ) => { data: DocumentDetail | undefined; isLoading: boolean; isError: boolean }
  pendingScrollRef?: React.MutableRefObject<string | null>
  pdfUrl?: (slug: string) => string
}) {
  const { data: doc, isLoading, isError } = useDocumentDetail(slug)

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (isError || !doc) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-destructive">Failed to load document</p>
      </div>
    )
  }

  return <CRContent doc={doc} pendingScrollRef={pendingScrollRef} pdfHref={pdfUrl?.(slug)} />
}

export function ClinicalReviewsListView({
  docs,
  isLoading,
  useDocumentDetail,
  pdfUrl,
}: ClinicalReviewsListViewProps) {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)
  const [query, setQuery] = useState("")
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const pendingScrollRef = useRef<string | null>(null)

  // Deep-link: resolve hash to the owning document on first load
  useEffect(() => {
    const hash = window.location.hash
    if (!hash.startsWith("#section-") || docs.length === 0) return
    const sectionKey = hash.replace("#section-", "")
    const match = docs.find((d) => d.section_keys?.includes(sectionKey))
    if (match && !selectedSlug) {
      pendingScrollRef.current = sectionKey
      setSelectedSlug(match.slug)
    }
  }, [docs]) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!query.trim()) return docs
    const q = query.toLowerCase()
    return docs.filter(
      (doc) =>
        doc.title.toLowerCase().includes(q) ||
        doc.keywords?.some((kw) => kw.toLowerCase().includes(q)) ||
        doc.compliance_tags?.some((tag) => tag.toLowerCase().includes(q))
    )
  }, [docs, query])

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  return (
    <div className="flex h-full">
      {/* Left: collapsible list panel */}
      <div className={`flex shrink-0 border-r ${sidebarCollapsed ? "" : "w-80"}`}>
        {!sidebarCollapsed && (
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="border-b px-5 py-4">
              <h1 className="text-xl font-semibold">Clinical Reviews</h1>
              {docs.length > 0 && (
                <div className="relative mt-3">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
              )}
            </div>
            <ScrollArea className="flex-1">
              <div className="space-y-2 p-4">
                {filtered.map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => setSelectedSlug(doc.slug)}
                    className={`flex w-full items-center justify-between rounded-lg border px-4 py-2.5 text-left text-sm transition-colors ${
                      selectedSlug === doc.slug
                        ? "border-primary bg-card text-foreground"
                        : "border-border bg-background hover:bg-accent"
                    }`}
                  >
                    <div>
                      <span className="font-medium">{doc.title}</span>
                      {doc.compliance_tags && doc.compliance_tags.length > 0 && (
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {doc.compliance_tags.map((tag) => (
                            <span
                              key={tag}
                              className="text-[10px] text-muted-foreground"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="ml-3 h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
        <button
          onClick={() => setSidebarCollapsed((prev) => !prev)}
          className="flex w-6 shrink-0 items-start justify-center pt-4 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {sidebarCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
      </div>

      {/* Right: content area (or empty state) */}
      {selectedSlug ? (
        <CRDetailPanel
          key={selectedSlug}
          slug={selectedSlug}
          useDocumentDetail={useDocumentDetail}
          pendingScrollRef={pendingScrollRef}
          pdfUrl={pdfUrl}
        />
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center text-muted-foreground">
          <FileText className="mb-3 h-12 w-12 opacity-30" />
          <p>Select an item to the left to see</p>
          <p>Clinical Review content</p>
        </div>
      )}
    </div>
  )
}
